import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { getDocumentUrl } from '../../utils/authUtils';

/**
 * Steps through every version of a document slot.
 *
 * Uploading a replacement no longer discards the file it replaced — the server
 * keeps the older copies — so each slot is a small stack. This renders that
 * stack: arrows (and swipe, on touch) to move between versions, a badge on the
 * one that's current, and a delete button for versions staff doesn't want.
 *
 * @param {Array}    versions   [{ path, current, uploadedAt, replacedAt, note }]
 * @param {string}   label      What the document is, e.g. "W-9"
 * @param {Function} onDelete   async (version) => void — omit to hide delete
 * @param {Function} onVersionChange (version) => void — fires as the selection
 *                   moves, so a host page can preview whichever version is showing
 * @param {boolean}  isReadOnly Hides delete when true
 */
function DocumentSelector({ versions = [], label = 'Document', onDelete, onVersionChange, isReadOnly = false, emptyText }) {
  const list = useMemo(() => (versions || []).filter((v) => v && v.path), [versions]);
  const [index, setIndex] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);

  // Keep the pointer inside the list when a version is added or removed
  useEffect(() => {
    setIndex((i) => Math.min(Math.max(i, 0), Math.max(list.length - 1, 0)));
  }, [list.length]);

  const active = list[index];

  // Let the host preview whichever version is on screen
  useEffect(() => {
    if (onVersionChange) onVersionChange(active || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active && active.path]);

  const formatDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
  };

  const fileNameOf = (p) => String(p).split(/[\\/]/).pop();

  const goPrev = () => setIndex((i) => (i > 0 ? i - 1 : i));
  const goNext = () => setIndex((i) => (i < list.length - 1 ? i + 1 : i));

  // Swipe support — the request was to swipe through versions on touch devices
  const handleTouchStart = (e) => setTouchStartX(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(delta) > 40) {
      if (delta < 0) goNext();
      else goPrev();
    }
    setTouchStartX(null);
  };

  const handleConfirmDelete = async () => {
    if (!active || !onDelete) return;
    setDeleting(true);
    try {
      await onDelete(active);
      setConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  if (!list.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {emptyText || `No ${label.toLowerCase()} uploaded yet.`}
      </Typography>
    );
  }

  const uploaded = formatDate(active.uploadedAt);
  const replaced = formatDate(active.replacedAt);

  return (
    <Box
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        backgroundColor: 'background.paper'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Newer version">
          <span>
            <IconButton size="small" onClick={goPrev} disabled={index === 0} aria-label="Newer version">
              <ChevronLeftIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <InsertDriveFileIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
              {fileNameOf(active.path)}
            </Typography>
            {active.current ? (
              <Chip label="Current" size="small" color="success" />
            ) : (
              <Chip label="Previous version" size="small" variant="outlined" />
            )}
            {active.verifiedByToken === false && (
              <Chip label="Unverified upload" size="small" color="warning" variant="outlined" />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Version {index + 1} of {list.length}
            {uploaded ? ` · Uploaded ${uploaded}` : ''}
            {!active.current && replaced ? ` · Replaced ${replaced}` : ''}
            {active.note ? ` · ${active.note}` : ''}
          </Typography>
        </Box>

        <Tooltip title="Older version">
          <span>
            <IconButton
              size="small"
              onClick={goNext}
              disabled={index >= list.length - 1}
              aria-label="Older version"
            >
              <ChevronRightIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button
          size="small"
          startIcon={<OpenInNewIcon />}
          href={getDocumentUrl(active.path)}
          target="_blank"
          rel="noopener noreferrer"
        >
          View
        </Button>
        {onDelete && !isReadOnly && (
          <Button
            size="small"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setConfirmOpen(true)}
          >
            Delete
          </Button>
        )}
      </Box>

      <Dialog open={confirmOpen} onClose={() => !deleting && setConfirmOpen(false)}>
        <DialogTitle>Delete this {label.toLowerCase()}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {active.current && list.length > 1
              ? `This is the current ${label.toLowerCase()}. Deleting it will make the next most recent version current.`
              : active.current
                ? `This is the only ${label.toLowerCase()} on file. The record will have no document after this.`
                : `This removes an older version. The current ${label.toLowerCase()} is unaffected.`}
          </DialogContentText>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {fileNameOf(active.path)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DocumentSelector;
